/* TGM CRO runtime (2026-09-24)
 * Shared, dependency-free helpers for the TGM CRO modules:
 *  - TGM.track(): light analytics via Shopify.analytics.publish (events prefixed with tgm_)
 *  - TGM.delivery: pure shipping/delivery date maths (Europe/Madrid, business days, cut-off)
 *  - <tgm-snap-carousel>: scroll-snap carousel controls (arrows, progress, keyboard, optional autoplay)
 *  - <tgm-delivery-date>: renders "Ships by {date}" / "Est. delivery {start} - {end}"
 *  - <tgm-cart-timer>: optional cart reservation countdown (sessionStorage, never loops)
 *  - [data-tgm-scroll-target]: smooth scroll to the reviews widget, offsetting the fixed header
 * All visible copy comes from Liquid (locale files / theme editor), never from this file.
 */
(function () {
  'use strict';

  var TGM = (window.TGM = window.TGM || {});

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  TGM.prefersReducedMotion = prefersReducedMotion;

  /* Time source. QA can set window.TGM_NOW (ISO string or Date) to simulate a moment. */
  TGM.now = function () {
    if (window.TGM_NOW) {
      var forced = new Date(window.TGM_NOW);
      if (!isNaN(forced.getTime())) return forced;
    }
    return new Date();
  };

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }
  TGM.escapeHtml = escapeHtml;

  /* ---------------------------------------------------------------- Analytics */
  TGM.track = function (name, payload) {
    if (!name) return;
    var eventName = name.indexOf('tgm_') === 0 ? name : 'tgm_' + name;
    var data = payload || {};
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, data);
      }
    } catch (error) {
      /* analytics must never break the storefront */
    }
    document.dispatchEvent(new CustomEvent('tgm:track', { detail: { name: eventName, payload: data } }));
  };

  document.addEventListener('click', function (event) {
    var el = event.target.closest && event.target.closest('[data-tgm-track]');
    if (!el) return;
    var payload = {};
    try {
      payload = JSON.parse(el.getAttribute('data-tgm-track-payload') || '{}');
    } catch (error) {
      payload = {};
    }
    TGM.track(el.getAttribute('data-tgm-track'), payload);
  });

  /* ---------------------------------------------------------------- Delivery dates */
  var DAY_MS = 86400000;

  function zonedParts(date, timeZone) {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'Europe/Madrid',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hourCycle: 'h23'
    }).formatToParts(date);
    var map = {};
    parts.forEach(function (part) {
      map[part.type] = part.value;
    });
    return { y: +map.year, m: +map.month, d: +map.day, hour: (+map.hour) % 24 };
  }

  function utcDay(y, m, d) {
    return new Date(Date.UTC(y, m - 1, d));
  }
  function addDays(date, n) {
    return new Date(date.getTime() + n * DAY_MS);
  }
  function isWeekend(date) {
    var day = date.getUTCDay();
    return day === 0 || day === 6;
  }
  function addBusinessDays(date, n, skipWeekends) {
    var result = date;
    var added = 0;
    while (added < n) {
      result = addDays(result, 1);
      if (!skipWeekends || !isWeekend(result)) added += 1;
    }
    return result;
  }
  function toNumber(value, fallback) {
    var n = parseInt(value, 10);
    return isFinite(n) ? n : fallback;
  }

  TGM.delivery = {
    /* Reads the data-* attributes rendered by snippets/tgm-delivery-attrs.liquid */
    configFrom: function (el) {
      var ds = el.dataset || {};
      var prepMin = toNumber(ds.prepMin, 5);
      var deliveryMin = toNumber(ds.deliveryMin, 18);
      return {
        prepMin: prepMin,
        prepMax: Math.max(prepMin, toNumber(ds.prepMax, 7)),
        deliveryMin: deliveryMin,
        deliveryMax: Math.max(deliveryMin, toNumber(ds.deliveryMax, 22)),
        cutoffHour: toNumber(ds.cutoffHour, 14),
        skipWeekends: ds.skipWeekends !== 'false',
        timeZone: ds.timeZone || 'Europe/Madrid',
        locale: ds.locale || document.documentElement.lang || 'en'
      };
    },

    /* Pure function: all returned dates are UTC midnights representing Madrid calendar days.
     * orderDay = today (Madrid) when it is a working day before the cut-off hour,
     * otherwise the next working day. Ship-by uses the maximum preparation time (conservative).
     * The delivery window counts calendar days from orderDay. */
    compute: function (now, cfg) {
      var t = zonedParts(now, cfg.timeZone);
      var orderDay = utcDay(t.y, t.m, t.d);
      var todayIsOff = cfg.skipWeekends && isWeekend(orderDay);
      if (t.hour >= cfg.cutoffHour || todayIsOff) {
        orderDay = addDays(orderDay, 1);
        while (cfg.skipWeekends && isWeekend(orderDay)) orderDay = addDays(orderDay, 1);
      }
      return {
        orderDay: orderDay,
        shipFrom: addBusinessDays(orderDay, cfg.prepMin, cfg.skipWeekends),
        shipBy: addBusinessDays(orderDay, cfg.prepMax, cfg.skipWeekends),
        deliveryStart: addDays(orderDay, cfg.deliveryMin),
        deliveryEnd: addDays(orderDay, cfg.deliveryMax)
      };
    },

    format: function (date, locale, style) {
      var options =
        style === 'short'
          ? { month: 'short', day: 'numeric', timeZone: 'UTC' }
          : { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' };
      try {
        return new Intl.DateTimeFormat(locale || 'en', options).format(date);
      } catch (error) {
        return new Intl.DateTimeFormat('en', options).format(date);
      }
    },

    isoDay: function (date) {
      return date.toISOString().slice(0, 10);
    }
  };

  function dateMarkup(date, locale, style) {
    return (
      '<strong><time datetime="' +
      TGM.delivery.isoDay(date) +
      '">' +
      escapeHtml(TGM.delivery.format(date, locale, style)) +
      '</time></strong>'
    );
  }

  /* Replaces {token} placeholders in an escaped template with trusted markup. */
  function fillTemplate(template, tokens) {
    var html = escapeHtml(template);
    Object.keys(tokens).forEach(function (key) {
      html = html.split('{' + key + '}').join(tokens[key]);
    });
    return html;
  }

  if (!customElements.get('tgm-delivery-date')) {
    customElements.define(
      'tgm-delivery-date',
      class extends HTMLElement {
        connectedCallback() {
          this.render();
        }
        render() {
          var template = this.getAttribute('data-template');
          var target = this.querySelector('[data-tgm-delivery-text]');
          if (!template || !target) return;
          var cfg = TGM.delivery.configFrom(this);
          var result = TGM.delivery.compute(TGM.now(), cfg);
          var html;
          if (this.getAttribute('data-mode') === 'window') {
            html = fillTemplate(template, {
              start: dateMarkup(result.deliveryStart, cfg.locale, 'short'),
              end: dateMarkup(result.deliveryEnd, cfg.locale, 'short')
            });
          } else {
            html = fillTemplate(template, { date: dateMarkup(result.shipBy, cfg.locale, 'long') });
          }
          target.innerHTML = html;
          this.setAttribute('data-ready', 'true');
        }
      }
    );
  }

  /* ---------------------------------------------------------------- Carousel */
  if (!customElements.get('tgm-snap-carousel')) {
    customElements.define(
      'tgm-snap-carousel',
      class extends HTMLElement {
        connectedCallback() {
          if (this._ready) return;
          this.viewport = this.querySelector('[data-tgm-carousel-viewport]');
          if (!this.viewport) return;
          this._ready = true;
          this.prevButton = this.querySelector('[data-tgm-carousel-prev]');
          this.nextButton = this.querySelector('[data-tgm-carousel-next]');
          this.bar = this.querySelector('[data-tgm-carousel-bar]');
          this.toggleButton = this.querySelector('[data-tgm-carousel-toggle]');

          this._onScroll = this.onScroll.bind(this);
          this._onKeydown = this.onKeydown.bind(this);
          this.viewport.addEventListener('scroll', this._onScroll, { passive: true });
          this.addEventListener('keydown', this._onKeydown);
          if (this.prevButton) this.prevButton.addEventListener('click', this.go.bind(this, -1));
          if (this.nextButton) this.nextButton.addEventListener('click', this.go.bind(this, 1));

          if ('ResizeObserver' in window) {
            this._resizeObserver = new ResizeObserver(this.update.bind(this));
            this._resizeObserver.observe(this.viewport);
          }

          var startIndex = parseInt(this.getAttribute('data-start-index'), 10);
          if (startIndex > 0) {
            var self = this;
            requestAnimationFrame(function () {
              self.revealSlide(startIndex, false);
            });
          }
          this.update();
          this.setupAutoplay();
        }

        disconnectedCallback() {
          if (this._resizeObserver) this._resizeObserver.disconnect();
          if (this._intersectionObserver) this._intersectionObserver.disconnect();
          clearInterval(this._autoplayTimer);
          this._ready = false;
        }

        slides() {
          return Array.prototype.slice.call(this.viewport.querySelectorAll('[data-tgm-carousel-slide]'));
        }

        stepSize() {
          var slides = this.slides();
          if (slides.length < 2) return this.viewport.clientWidth;
          return Math.abs(slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left) || this.viewport.clientWidth;
        }

        go(direction) {
          var step = this.stepSize();
          var first = this.slides()[0];
          var gap = first ? Math.max(0, step - first.getBoundingClientRect().width) : 0;
          var perView = Math.max(1, Math.floor((this.viewport.clientWidth + gap + 2) / step));
          this.viewport.scrollBy({ left: direction * step * perView, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }

        onScroll() {
          if (this._raf) return;
          var self = this;
          this._raf = requestAnimationFrame(function () {
            self._raf = null;
            self.update();
          });
        }

        update() {
          var v = this.viewport;
          var max = v.scrollWidth - v.clientWidth;
          var x = Math.abs(v.scrollLeft);
          var overflowing = max > 2;
          this.classList.toggle('is-overflowing', overflowing);
          if (this.prevButton) this.prevButton.disabled = !overflowing || x <= 2;
          if (this.nextButton) this.nextButton.disabled = !overflowing || x >= max - 2;
          if (this.bar) {
            var ratio = overflowing ? Math.min(1, (x + v.clientWidth) / v.scrollWidth) : 1;
            this.bar.style.transform = 'scaleX(' + ratio.toFixed(4) + ')';
          }
        }

        /* Scrolls the viewport horizontally only, so the page never jumps. */
        revealSlide(index, smooth) {
          var slide = this.slides()[index];
          if (!slide) return;
          var v = this.viewport;
          var vr = v.getBoundingClientRect();
          var sr = slide.getBoundingClientRect();
          var left = v.scrollLeft;
          if (this.getAttribute('data-align') === 'center') {
            left += sr.left - vr.left - (vr.width - sr.width) / 2;
          } else if (sr.left < vr.left) {
            left += sr.left - vr.left;
          } else if (sr.right > vr.right) {
            left += sr.right - vr.right;
          }
          v.scrollTo({ left: Math.max(0, left), behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto' });
        }

        onKeydown(event) {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          var slides = this.slides();
          var current = -1;
          for (var i = 0; i < slides.length; i++) {
            if (slides[i].contains(document.activeElement)) current = i;
          }
          if (current === -1) return;
          var nextIndex = current + (event.key === 'ArrowRight' ? 1 : -1);
          var target = slides[nextIndex];
          if (!target) return;
          event.preventDefault();
          var focusable = target.querySelector('a[href], button:not([disabled]), [tabindex]') || target;
          focusable.focus({ preventScroll: true });
          this.revealSlide(nextIndex, true);
        }

        setupAutoplay() {
          var seconds = parseInt(this.getAttribute('data-autoplay'), 10);
          if (!seconds || prefersReducedMotion()) {
            if (this.toggleButton) this.toggleButton.hidden = true;
            return;
          }
          var self = this;
          this._visible = false;
          this._userPaused = false;
          this._interactionPaused = false;

          var pause = function () { self._interactionPaused = true; };
          var resume = function () { self._interactionPaused = false; };
          this.addEventListener('mouseenter', pause);
          this.addEventListener('mouseleave', resume);
          this.addEventListener('focusin', pause);
          this.addEventListener('focusout', function (event) {
            if (!self.contains(event.relatedTarget)) resume();
          });
          this.viewport.addEventListener('pointerdown', function () { self._userPaused = true; self.syncToggle(); }, { passive: true });

          if (this.toggleButton) {
            this.toggleButton.hidden = false;
            this.toggleButton.addEventListener('click', function () {
              self._userPaused = !self._userPaused;
              self.syncToggle();
            });
            this.syncToggle();
          }

          if ('IntersectionObserver' in window) {
            this._intersectionObserver = new IntersectionObserver(function (entries) {
              self._visible = entries[0].isIntersecting;
            }, { threshold: 0.4 });
            this._intersectionObserver.observe(this);
          } else {
            this._visible = true;
          }

          this._autoplayTimer = setInterval(function () {
            if (self._userPaused || self._interactionPaused || !self._visible || document.hidden) return;
            var v = self.viewport;
            if (v.scrollLeft >= v.scrollWidth - v.clientWidth - 2) {
              v.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
              self.go(1);
            }
          }, seconds * 1000);
        }

        syncToggle() {
          if (!this.toggleButton) return;
          var paused = this._userPaused;
          this.toggleButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
          this.toggleButton.setAttribute('aria-label', this.toggleButton.getAttribute(paused ? 'data-label-play' : 'data-label-pause'));
          this.toggleButton.classList.toggle('is-paused', paused);
        }
      }
    );
  }

  /* ---------------------------------------------------------------- In-view video
   * Muted looping video that only downloads and plays while visible, with a pause/play button
   * (WCAG 2.2.2). No autoplay for shoppers who prefer reduced motion. */
  if (!customElements.get('tgm-inview-video')) {
    customElements.define(
      'tgm-inview-video',
      class extends HTMLElement {
        connectedCallback() {
          this.video = this.querySelector('video');
          if (!this.video || this._ready) return;
          this._ready = true;
          this.video.muted = true;
          this.video.setAttribute('muted', '');
          this.userPaused = !(this.hasAttribute('data-autoplay') && !prefersReducedMotion());
          this.toggle = this.querySelector('[data-tgm-video-toggle]');
          var self = this;
          if (this.toggle) {
            this.toggle.hidden = false;
            this.toggle.addEventListener('click', function () {
              if (self.video.paused) {
                self.userPaused = false;
                self.play();
              } else {
                self.userPaused = true;
                self.video.pause();
              }
            });
          }
          this.video.addEventListener('play', this.sync.bind(this));
          this.video.addEventListener('pause', this.sync.bind(this));
          if ('IntersectionObserver' in window) {
            this._io = new IntersectionObserver(function (entries) {
              if (entries[0].isIntersecting) {
                if (!self.userPaused) self.play();
              } else if (!self.video.paused) {
                self.video.pause();
              }
            }, { threshold: 0.35 });
            this._io.observe(this);
          }
          this.sync();
        }
        disconnectedCallback() {
          if (this._io) this._io.disconnect();
          this._ready = false;
        }
        play() {
          var promise = this.video.play();
          if (promise && promise.catch) promise.catch(function () {});
        }
        sync() {
          var playing = !this.video.paused;
          this.classList.toggle('is-playing', playing);
          if (this.toggle) {
            this.toggle.setAttribute('aria-label', this.getAttribute(playing ? 'data-label-pause' : 'data-label-play') || '');
          }
        }
      }
    );
  }

  /* Theme editor: bring the selected slide into view. */
  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target && event.target.closest && event.target.closest('[data-tgm-carousel-slide]');
    var carousel = slide && slide.closest('tgm-snap-carousel');
    if (!carousel || typeof carousel.slides !== 'function') return;
    carousel.revealSlide(carousel.slides().indexOf(slide), true);
  });

  /* ---------------------------------------------------------------- Cart reservation timer */
  var TIMER_KEY = 'tgm_cart_reservation_v1';

  function readTimerState() {
    try {
      var raw = window.sessionStorage.getItem(TIMER_KEY);
      return { ok: true, state: raw ? JSON.parse(raw) : null };
    } catch (error) {
      return { ok: false, state: null };
    }
  }
  function writeTimerState(state) {
    try {
      window.sessionStorage.setItem(TIMER_KEY, JSON.stringify(state));
      return true;
    } catch (error) {
      return false;
    }
  }

  if (!customElements.get('tgm-cart-timer')) {
    customElements.define(
      'tgm-cart-timer',
      class extends HTMLElement {
        connectedCallback() {
          var minutes = parseInt(this.getAttribute('data-minutes'), 10) || 10;
          var read = readTimerState();
          /* Without sessionStorage the countdown could restart on every page: do not show it at all. */
          if (!read.ok) return this.remove();
          var state = read.state;
          if (!state || typeof state.start !== 'number') {
            state = { start: Date.now(), expired: false };
            if (!writeTimerState(state)) return this.remove();
          }
          if (state.expired) return this.remove();
          this._state = state;
          this._deadline = state.start + minutes * 60000;
          this._time = this.querySelector('[data-tgm-timer-time]');
          if (!this.tick()) return;
          this.hidden = false;
          this._interval = setInterval(this.tick.bind(this), 1000);
        }
        disconnectedCallback() {
          clearInterval(this._interval);
        }
        tick() {
          var remaining = this._deadline - Date.now();
          if (remaining <= 0) {
            clearInterval(this._interval);
            this._state.expired = true;
            writeTimerState(this._state);
            this.remove();
            return false;
          }
          var m = Math.floor(remaining / 60000);
          var s = Math.floor((remaining % 60000) / 1000);
          if (this._time) this._time.textContent = m + ':' + (s < 10 ? '0' : '') + s;
          return true;
        }
      }
    );
  }

  /* ---------------------------------------------------------------- Scroll to reviews */
  TGM.headerOffset = function () {
    var header = document.querySelector('[data-tgm-header], sticky-header, .section-header');
    if (!header) return 0;
    var style = window.getComputedStyle(header);
    if (style.position !== 'fixed' && style.position !== 'sticky') return 0;
    return header.getBoundingClientRect().height;
  };

  TGM.findScrollTarget = function (selectorList) {
    if (!selectorList) return null;
    var selectors = selectorList.split(',');
    for (var i = 0; i < selectors.length; i++) {
      var selector = selectors[i].trim();
      if (!selector) continue;
      try {
        var el = document.querySelector(selector);
        if (el) return el;
      } catch (error) {
        /* invalid selector typed in the editor: ignore it */
      }
    }
    return null;
  };

  TGM.scrollToElement = function (el) {
    var top = el.getBoundingClientRect().top + window.pageYOffset - TGM.headerOffset() - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  };

  /* After apps have rendered, a rating link without any reviews widget on the page becomes plain text. */
  window.addEventListener('load', function () {
    setTimeout(function () {
      document.querySelectorAll('a[data-tgm-scroll-target]').forEach(function (link) {
        if (TGM.findScrollTarget(link.getAttribute('data-tgm-scroll-target'))) return;
        link.removeAttribute('href');
        link.removeAttribute('data-tgm-track');
      });
    }, 1500);
  });

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-tgm-scroll-target]');
    if (!link) return;
    var target = TGM.findScrollTarget(link.getAttribute('data-tgm-scroll-target'));
    if (!target) return;
    event.preventDefault();
    TGM.scrollToElement(target);
  });
})();

// D (26-09-2026) · Mobile product page: measure what sits between the gallery and the Add to cart
// and publish it as --tgm-pdp-reserved, so the CSS can size the gallery to the screen.
(function () {
  var mq = window.matchMedia('(max-width: 749px)');
  document.querySelectorAll('[id^="MainProduct-"]').forEach(function (section) {
    var media = section.querySelector('.product__media-wrapper');
    var info = section.querySelector('.product__info-container');
    if (!media || !info) return;
    var lastWidth = window.innerWidth;
    function update() {
      var btn = section.querySelector('product-form .product-form__submit');
      if (!mq.matches || !btn) { section.style.removeProperty('--tgm-pdp-reserved'); return; }
      var m = media.getBoundingClientRect();
      var b = btn.getBoundingClientRect();
      if (!b.height) return;
      var reserved = Math.ceil(m.top + window.scrollY + (b.bottom - m.bottom) + 8);
      section.style.setProperty('--tgm-pdp-reserved', reserved + 'px');
    }
    update();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(info);
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastWidth) return; // ignore iOS toolbar show/hide
      lastWidth = window.innerWidth;
      update();
    });
  });
})();

// D (26-09-2026) · The Shopify Inbox bubble never covers purchase controls on mobile: it hides only
// the closed bubble (::part(activator)) while the main Add to cart or the bottom of a Kickflip
// customizer crosses its band. The sticky bar lifts it with --shopify-chat-activator-offset.
(function () {
  var mq = window.matchMedia('(max-width: 768px)');
  var root = document.documentElement;
  var raf = 0;
  function zones() {
    var list = [];
    var main = document.querySelector('product-form[data-main="true"] .product-form__submit, .main-product-form .product-form__submit');
    if (main) list.push(main.getBoundingClientRect());
    document.querySelectorAll('.kickflip-app-block kickflip-customizer-iframe').forEach(function (k) {
      var r = k.getBoundingClientRect();
      if (r.height) list.push({ top: Math.max(r.top, r.bottom - 110), bottom: r.bottom, height: r.height });
    });
    return list;
  }
  function sync() {
    raf = 0;
    if (!mq.matches) { root.classList.remove('tgm-chat-yield'); return; }
    var chat = document.querySelector('shopify-chat') || root;
    var offset = parseFloat(getComputedStyle(chat).getPropertyValue('--shopify-chat-activator-offset')) || 20;
    offset += parseFloat(getComputedStyle(root).getPropertyValue('--tgm-consent-h')) || 0; // H: lifted above the cookie banner
    var bandBottom = window.innerHeight - offset + 8;
    var bandTop = window.innerHeight - offset - 46 - 8;
    var hit = zones().some(function (r) { return r.height > 0 && r.top < bandBottom && r.bottom > bandTop; });
    root.classList.toggle('tgm-chat-yield', hit);
  }
  function queue() { if (!raf) raf = requestAnimationFrame(sync); }
  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);
  window.addEventListener('load', queue);
  document.addEventListener('tgm:sticky-atc', queue);
  document.addEventListener('tgm:consent', queue);
  queue();
})();

// H (26-09-2026) · Shopify cookie banner (the only one left): while it is open the page knows its height
// (--tgm-consent-h, html.tgm-consent-open) so the sticky Add to cart and the chat sit above it and the
// end of the page can be scrolled clear of it. Nothing here accepts, declines or changes consent.
(function () {
  var root = document.documentElement;
  var watched = null;
  var raf = 0;
  function isOpen(el) {
    if (!el || !el.isConnected) return false;
    var cs = getComputedStyle(el);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && el.offsetHeight > 0;
  }
  function sync() {
    raf = 0;
    var banner = document.getElementById('shopify-pc__banner');
    if (banner && banner !== watched) {
      watched = banner;
      new MutationObserver(queue).observe(banner, { attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
      if (window.ResizeObserver) new ResizeObserver(queue).observe(banner);
    }
    var open = isOpen(banner);
    var h = open ? Math.ceil(banner.getBoundingClientRect().height) + 'px' : '0px';
    if (root.classList.contains('tgm-consent-open') === open && root.style.getPropertyValue('--tgm-consent-h') === h) return;
    root.classList.toggle('tgm-consent-open', open);
    root.style.setProperty('--tgm-consent-h', h);
    document.dispatchEvent(new CustomEvent('tgm:consent', { detail: { open: open } }));
  }
  function queue() { if (!raf) raf = requestAnimationFrame(sync); }
  new MutationObserver(queue).observe(document.body || document.documentElement, { childList: true });
  window.addEventListener('resize', queue);
  window.addEventListener('load', queue);
  queue();
})();
